(() => {
  const root = document.querySelector('[data-lulu-root]');
  if (!root) return;

  const launcher = root.querySelector('[data-lulu-launcher]');
  const panel = root.querySelector('[data-lulu-panel]');
  const closeButton = root.querySelector('[data-lulu-close]');
  const messages = root.querySelector('[data-lulu-messages]');
  const form = root.querySelector('[data-lulu-form]');
  const input = root.querySelector('[data-lulu-input]');
  const nudge = root.querySelector('[data-lulu-nudge]');
  const nudgeClose = root.querySelector('[data-lulu-nudge-close]');
  const autoOpen = root.dataset.luluAutoOpen === "1";
  const triggerNudge = root.dataset.luluTriggerNudge === "1";
  root.dataset.luluReady = "1";
  root.dataset.luluLoading = "0";
  launcher?.removeAttribute("aria-busy");

  const responses = {
    estimate: {
      text: "The best answer depends on your actual home, utility, roof, electricity use, and goals. LuNova’s estimate starts with the property instead of assuming every Illinois home is the same.",
      links: [
        ["Check my home", "/pages/start-solar-estimate"],
        ["How it works", "/#how-it-works"]
      ]
    },
    incentives: {
      text: "Illinois has solar programs and incentives, but values and eligibility can change. LuNova can help you understand what may apply to your property, while current program terms should always be confirmed before you make a decision.",
      links: [
        ["Illinois solar incentives", "/pages/illinois-solar-incentives"],
        ["Solar questions", "/pages/solar-questions"]
      ]
    },
    battery: {
      text: "A battery is not automatically the right choice for every home. It depends on your utility, backup-power goals, rate structure, system design, and budget. LuNova should evaluate those details before recommending storage.",
      links: [
        ["Battery guide", "/pages/illinois-solar-battery"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    bill: {
      text: "Your electric bill is one of the most useful starting points because it shows how much energy your home is actually using. LuNova can use your usage information to build a more realistic solar estimate.",
      links: [
        ["Start my estimate", "/pages/start-solar-estimate"],
        ["Solar questions", "/pages/solar-questions"]
      ]
    },
    financing: {
      text: "Solar can be structured in different ways, including cash and third-party financing options. The right comparison depends on payment terms, ownership, incentives, long-term cost, and your goals. LuNova can walk you through the tradeoffs without pressure.",
      links: [
        ["Solar financing", "/pages/illinois-solar-financing"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    cwlp: {
      text: "CWLP is a municipal utility, so Springfield homeowners should not assume Ameren or ComEd rules apply. System sizing and economics should be reviewed using your actual CWLP usage and the utility’s current requirements.",
      links: [
        ["Springfield solar guide", "/pages/springfield-il-solar"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    ameren: {
      text: "Ameren’s solar billing and interconnection rules are different from other Illinois utilities and can change over time. LuNova should evaluate the current rules together with your actual usage before recommending system size or battery storage.",
      links: [
        ["Ameren solar guide", "/pages/ameren-illinois-solar"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    comed: {
      text: "ComEd has its own solar and interconnection rules. A good recommendation should use your actual usage, property, and current ComEd requirements rather than a generic Illinois assumption.",
      links: [
        ["ComEd solar guide", "/pages/comed-illinois-solar"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    winter: {
      text: "Solar panels can still produce electricity during an Illinois winter. Shorter days, weather, roof orientation, shading, and snow can affect production, which is why annual production modeling matters more than one winter day.",
      links: [
        ["Illinois solar news", "/blogs/news"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    process: {
      text: "LuNova starts with your home and energy use, designs around the property, reviews the proposal with you, and then moves into installation and utility approval if you decide to proceed.",
      links: [
        ["Check my home", "/pages/start-solar-estimate"],
        ["Contact LuNova", "/pages/contact"]
      ]
    },
    contact: {
      text: "I can point you in the right direction, but if you want a person, the LuNova team can help directly.",
      links: [
        ["Contact LuNova", "/pages/contact"],
        ["Check my home", "/pages/start-solar-estimate"]
      ]
    },
    fallback: {
      text: "I don’t want to guess about that. For anything property-specific, contractual, tax-related, financing-specific, or dependent on a current utility rule, the safest next step is to use LuNova’s homeowner resources or ask the team directly.",
      links: [
        ["Solar questions", "/pages/solar-questions"],
        ["Contact LuNova", "/pages/contact"]
      ]
    }
  };

  const escapeText = (value) => String(value || "").trim();

  function addMessage(kind, text, links = []) {
    const wrap = document.createElement("div");
    wrap.className = "ln-lulu__message ln-lulu__message--" + kind;

    const bubble = document.createElement("div");
    bubble.className = "ln-lulu__bubble";

    const p = document.createElement("p");
    p.textContent = text;
    bubble.appendChild(p);

    if (links.length) {
      const linkWrap = document.createElement("div");
      linkWrap.className = "ln-lulu__links";
      links.forEach(([label, href]) => {
        const a = document.createElement("a");
        a.href = href;
        a.textContent = label;
        const arrow = document.createElement("span");
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = "→";
        a.appendChild(arrow);
        linkWrap.appendChild(a);
      });
      bubble.appendChild(linkWrap);
    }

    wrap.appendChild(bubble);
    messages.appendChild(wrap);
    messages.scrollTop = messages.scrollHeight;
  }

  function classify(raw) {
    const q = raw.toLowerCase();

    if (/cwlp|city water light|springfield utility/.test(q)) return "cwlp";
    if (/ameren/.test(q)) return "ameren";
    if (/comed|com ed/.test(q)) return "comed";
    if (/battery|powerwall|backup|storage/.test(q)) return "battery";
    if (/incentive|rebate|illinois shines|rec|credit|tax/.test(q)) return "incentives";
    if (/finance|financing|loan|ppa|lease|cash|payment/.test(q)) return "financing";
    if (/bill|kwh|usage|electricity use|electric bill/.test(q)) return "bill";
    if (/winter|snow|cold|december|january/.test(q)) return "winter";
    if (/process|how it works|install|installation|timeline|permit/.test(q)) return "process";
    if (/contact|person|human|representative|call|talk to/.test(q)) return "contact";
    if (/estimate|quote|my home|worth it|make sense|cost/.test(q)) return "estimate";

    return "fallback";
  }

  function answer(intent) {
    const result = responses[intent] || responses.fallback;
    window.setTimeout(() => addMessage("bot", result.text, result.links), 220);
  }

  function ask(text, intent) {
    const clean = escapeText(text);
    if (!clean) return;
    addMessage("user", clean);
    answer(intent || classify(clean));
  }

  function open() {
    panel.hidden = false;
    root.classList.add("is-open");
    panel.setAttribute("aria-hidden", "false");
    panel.removeAttribute("inert");
    launcher.setAttribute("aria-expanded", "true");
    if (nudge) nudge.hidden = true;
    try { sessionStorage.setItem("lnLuluNudgeSeen", "1"); } catch (e) {}
    window.setTimeout(() => input && input.focus(), 120);
  }

  function close() {
    root.classList.remove("is-open");
    panel.setAttribute("aria-hidden", "true");
    panel.setAttribute("inert", "");
    launcher.setAttribute("aria-expanded", "false");
    launcher.focus();
  }

  launcher.addEventListener("click", () => {
    root.classList.contains("is-open") ? close() : open();
  });

  closeButton.addEventListener("click", close);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const value = escapeText(input.value);
    if (!value) return;
    input.value = "";
    ask(value);
  });

  root.querySelectorAll("[data-lulu-quick]").forEach((button) => {
    button.addEventListener("click", () => {
      const intent = button.getAttribute("data-lulu-quick");
      ask(button.textContent, intent);
    });
  });

  if (nudgeClose) {
    nudgeClose.addEventListener("click", () => {
      nudge.hidden = true;
      try { sessionStorage.setItem("lnLuluNudgeSeen", "1"); } catch (e) {}
    });
  }

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && root.classList.contains("is-open")) close();
  });

  let nudgeShown = false;

  function showNudge() {
    if (nudgeShown || root.classList.contains("is-open") || !nudge) return;

    try {
      if (sessionStorage.getItem("lnLuluNudgeSeen") === "1") return;
    } catch (e) {}

    nudgeShown = true;
    nudge.hidden = false;

    window.setTimeout(() => {
      if (nudge && !root.classList.contains("is-open")) nudge.hidden = true;
    }, 7000);
  }

  if (autoOpen) {
    window.setTimeout(open, 0);
  } else if (triggerNudge) {
    window.setTimeout(showNudge, 80);
  }
})();
